/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, DataTable, HFlow, Text } from 'bold-ui'
import { usePecField } from 'components/form/final-form/hooks/useField'
import { DisponibilidadeHorusEnum } from 'graphql/types.generated'
import { MetaRoot } from 'util/metaPath'

import DisponibilidadeHorus from '../../../components/DisponibilidadeHorus'
import { ConsultaHorusItemType } from '../../../hooks/usePrescricaoHorusIntegration'
import { MedicamentoFormModel } from '../../../model-prescricao'
import { calculaDisponibilidadeHorus, enumToStringDisponibilidadeHorus } from '../../../utils/utils-prescricao'

interface PrescricaoMedicamentoHorusInfoCellSectionProps {
  name: MetaRoot<MedicamentoFormModel>
  horusData?: ConsultaHorusItemType[]
  statusConsulta?: DisponibilidadeHorusEnum
  isHorusLoading: boolean
  loadingConexaoInternet: boolean
  executeConexaoQuery: () => void
}

export function PrescricaoMedicamentoHorusInfoCellSection(
  props: Readonly<PrescricaoMedicamentoHorusInfoCellSectionProps>
) {
  const { name, horusData, statusConsulta, isHorusLoading, loadingConexaoInternet, executeConexaoQuery } = props

  const { input: registroManualInput } = usePecField({ name: name.registroManual, subscription: { value: true } })
  const isRegistroManual = registroManualInput.value

  const { input: principioAtivoTextInput } = usePecField({
    name: name.principioAtivoText,
    subscription: { value: true },
  })
  const principioAtivoText = principioAtivoTextInput.value

  const { input: principioAtivoInput } = usePecField({ name: name.principioAtivoCombo, subscription: { value: true } })
  const principioAtivoCombo = principioAtivoInput.value

  const { input: quantidadeInput } = usePecField({ name: name.quantidade, subscription: { value: true } })
  const quantidade = quantidadeInput.value

  if (isRegistroManual && principioAtivoText) {
    return (
      <Cell size={12} style={styles.centerText}>
        <Text fontWeight='bold'>{enumToStringDisponibilidadeHorus[DisponibilidadeHorusEnum.DESABILITADO_MANUAL]}</Text>
      </Cell>
    )
  }

  if (!isRegistroManual && !principioAtivoCombo) {
    return null
  }

  switch (statusConsulta) {
    case DisponibilidadeHorusEnum.SUCESSO_CONEXAO:
      if (horusData) {
        return (
          <Cell size={12} style={styles.marginTop}>
            <DataTable<ConsultaHorusItemType>
              rows={horusData}
              loading={isHorusLoading}
              columns={[
                {
                  name: 'estabelecimento',
                  header: 'Estabelecimento',
                  render: (item) => item?.unidadeSaude?.nome,
                  style: { width: '35%' },
                },
                {
                  name: 'disponibilidade',
                  header: 'Disponibilidade',
                  render: (item) => item?.quantidadeDisponivel,
                },
                {
                  name: 'status',
                  header: 'Status',
                  render: (item) => (
                    <DisponibilidadeHorus
                      disponibilidade={calculaDisponibilidadeHorus(item?.quantidadeDisponivel, quantidade)}
                      isOnModal
                    />
                  ),
                },
              ]}
            />
          </Cell>
        )
      }
      return null

    case DisponibilidadeHorusEnum.DESABILITADO:
      return (
        <Cell size={12} style={styles.centerText}>
          <Text fontWeight='bold'>{enumToStringDisponibilidadeHorus[DisponibilidadeHorusEnum.DESABILITADO]}</Text>
        </Cell>
      )
    default:
      if (!statusConsulta) {
        return null
      }
      return (
        <Cell size={12} style={styles.centerText}>
          <HFlow alignItems='center'>
            <Button kind='primary' size='small' loading={loadingConexaoInternet} onClick={executeConexaoQuery}>
              Tentar novamente
            </Button>
            <DisponibilidadeHorus disponibilidade={DisponibilidadeHorusEnum.SEM_CONEXAO} isOnModal />
          </HFlow>
        </Cell>
      )
  }
}

const styles = {
  centerText: css`
    text-align: center;
    margin-top: 2rem;
  `,
  marginTop: css`
    margin-top: 2rem;
  `,
}
